import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './RouteMap.css';

/**
 * OpenStreetMap-backed map for the private home.
 *
 * Built on vanilla Leaflet (not react-leaflet) on purpose: CRA's Jest resolver
 * chokes on packages that expose ESM-only `exports` subpaths (same class of
 * problem that ruled out react-router v7), and vanilla Leaflet sidesteps it.
 * All markers are HTML `divIcon`s so we never depend on Leaflet's bundled PNG
 * marker assets (whose paths break under webpack).
 *
 * The map is NOT created on mount: we first resolve the geolocation decision.
 *  - permission already granted → locate and open the map on the user.
 *  - permission still "prompt"  → show a gate; the map opens once the user
 *    answers the browser prompt (accept → their position, reject → fallback).
 *  - permission already denied  → open the map on the fallback right away.
 * The fallback is the Ciudad de las Artes y las Ciencias (Valencia). Satellite
 * imagery is the default layer.
 */

// Ciudad de las Artes y las Ciencias, València — the fallback centre used when
// the user declines (or can't share) their location.
const FALLBACK_CENTER = [39.4547, -0.3524];
const DEFAULT_ZOOM = 13;

// Satellite is the default layer.
const INITIAL_LAYER = 'satelite';

// Tile sources. "Relieve" is the plain OpenStreetMap raster; the other two are
// OSM-derived / imagery alternates.
const LAYERS = {
  relieve: {
    label: 'Relieve',
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; OpenStreetMap contributors',
    maxZoom: 19,
  },
  satelite: {
    label: 'Satélite',
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: 'Tiles &copy; Esri, Maxar, Earthstar Geographics',
    maxZoom: 19,
  },
  topografico: {
    label: 'Topográfico',
    url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
    attribution: 'Map data: &copy; OpenStreetMap, SRTM | &copy; OpenTopoMap (CC-BY-SA)',
    maxZoom: 17,
  },
};

const LAYER_ORDER = ['relieve', 'satelite', 'topografico'];

// Sample points of interest (route heads) sprinkled around the centre so the
// map is not empty. Offsets are relative to whatever centre we end up on.
const POI_OFFSETS = [
  [0.028, 0.035],
  [-0.02, 0.05],
  [0.015, -0.045],
  [-0.035, -0.02],
];

const GEO_OPTIONS = { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 };

const userIcon = L.divIcon({
  className: 'flik-user-marker',
  html: '<span class="flik-user-marker__pulse"></span><span class="flik-user-marker__dot"></span>',
  iconSize: [26, 26],
  iconAnchor: [13, 13],
});

const poiIcon = L.divIcon({
  className: 'flik-poi-marker',
  html: '<span class="flik-poi-marker__dot"></span>',
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});

const RouteMap = forwardRef(function RouteMap(_props, ref) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const tileRef = useRef(null);
  const userMarkerRef = useRef(null);
  const [activeLayer, setActiveLayer] = useState(INITIAL_LAYER);
  const [locating, setLocating] = useState(false);

  // 'init'    → deciding whether to show the gate (checking permission)
  // 'prompt'  → waiting for the user to allow/skip location
  // 'locating'→ browser prompt open / fetching the position
  // 'ready'   → map created and visible
  const [phase, setPhase] = useState('init');

  /* ------------------------------ map setup --------------------------- */

  // Creates the Leaflet map centred on `center`, drops the sample POIs and a
  // "you are here" marker, then reveals it. Guards against StrictMode's double
  // invocation and against a center resolving after unmount.
  function createMap(center) {
    if (mapRef.current || !containerRef.current) return;

    const map = L.map(containerRef.current, {
      center,
      zoom: DEFAULT_ZOOM,
      zoomControl: false, // we render our own controls to match the design
      attributionControl: true,
    });
    mapRef.current = map;

    const initial = LAYERS[INITIAL_LAYER];
    tileRef.current = L.tileLayer(initial.url, {
      attribution: initial.attribution,
      maxZoom: initial.maxZoom,
    }).addTo(map);

    // Nudge the attribution into a compact, on-brand corner.
    map.attributionControl.setPrefix('');

    // Drop a few sample route heads around the resolved centre.
    POI_OFFSETS.forEach(([dLat, dLng]) => {
      L.marker([center[0] + dLat, center[1] + dLng], {
        icon: poiIcon,
        keyboard: false,
      }).addTo(map);
    });

    // Mark the centre as "you are here" (real position or fallback).
    placeUserMarker(center);

    setPhase('ready');

    // Leaflet occasionally needs a nudge once its flex container has sized.
    setTimeout(() => {
      if (mapRef.current === map) map.invalidateSize();
    }, 200);
  }

  // On mount, decide whether we can open the map straight away or must ask.
  useEffect(() => {
    let alive = true;

    if (!('geolocation' in navigator)) {
      createMap(FALLBACK_CENTER);
      return undefined;
    }

    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions
        .query({ name: 'geolocation' })
        .then((res) => {
          if (!alive) return;
          if (res.state === 'granted') {
            requestLocation(); // already trusted us — locate immediately
          } else if (res.state === 'denied') {
            createMap(FALLBACK_CENTER); // already refused — go to the fallback
          } else {
            setPhase('prompt'); // undecided — show the gate
          }
        })
        .catch(() => {
          if (alive) setPhase('prompt');
        });
    } else {
      // No Permissions API: require an explicit gesture via the gate.
      setPhase('prompt');
    }

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tear the map down on unmount.
  useEffect(() => {
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        tileRef.current = null;
        userMarkerRef.current = null;
      }
    };
  }, []);

  /* ------------------------------ location ---------------------------- */

  // Requests the position and opens the map: on success at the user, on
  // denial/failure at the Valencia fallback. Used both for the initial gate and
  // for the "granted" fast path.
  function requestLocation() {
    if (!('geolocation' in navigator)) {
      createMap(FALLBACK_CENTER);
      return;
    }
    setPhase('locating');
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        createMap([pos.coords.latitude, pos.coords.longitude]);
        setLocating(false);
      },
      () => {
        createMap(FALLBACK_CENTER);
        setLocating(false);
      },
      GEO_OPTIONS
    );
  }

  function skipLocation() {
    createMap(FALLBACK_CENTER);
  }

  function placeUserMarker(latlng) {
    const map = mapRef.current;
    if (!map) return;
    if (userMarkerRef.current) {
      userMarkerRef.current.setLatLng(latlng);
    } else {
      userMarkerRef.current = L.marker(latlng, {
        icon: userIcon,
        zIndexOffset: 1000,
        keyboard: false,
      }).addTo(map);
    }
  }

  // Re-centre on the live position (the crosshair button, once the map exists).
  function recenter() {
    const map = mapRef.current;
    if (!map || !('geolocation' in navigator)) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (mapRef.current !== map) return;
        const latlng = [pos.coords.latitude, pos.coords.longitude];
        placeUserMarker(latlng);
        map.setView(latlng, DEFAULT_ZOOM, { animate: true });
        setLocating(false);
      },
      () => {
        if (mapRef.current !== map) return;
        setLocating(false);
      },
      GEO_OPTIONS
    );
  }

  /* ------------------------------ layers ------------------------------ */

  function switchLayer(key) {
    const map = mapRef.current;
    if (!map || key === activeLayer) return;
    const next = LAYERS[key];
    if (tileRef.current) map.removeLayer(tileRef.current);
    tileRef.current = L.tileLayer(next.url, {
      attribution: next.attribution,
      maxZoom: next.maxZoom,
    }).addTo(map);
    setActiveLayer(key);
  }

  // Let the parent (the mobile "M" button lives in HomePage's search row)
  // cycle through the map styles without lifting the tile-management state.
  useImperativeHandle(
    ref,
    () => ({
      cycleLayer() {
        const i = LAYER_ORDER.indexOf(activeLayer);
        switchLayer(LAYER_ORDER[(i + 1) % LAYER_ORDER.length]);
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeLayer]
  );

  const handleZoomIn = () => mapRef.current && mapRef.current.zoomIn();
  const handleZoomOut = () => mapRef.current && mapRef.current.zoomOut();

  const ready = phase === 'ready';

  return (
    <div className={`RouteMap RouteMap--${activeLayer}`}>
      <div ref={containerRef} className="RouteMap__canvas" aria-label="Mapa de rutas" />

      {/* Location gate — covers the map until the geolocation decision is made. */}
      {!ready && (
        <div className="RouteMap__gate">
          {phase === 'prompt' ? (
            <div className="RouteMap__gateCard">
              <span className="RouteMap__gateIcon" aria-hidden="true">
                <PinIcon />
              </span>
              <h3 className="RouteMap__gateTitle">Activa tu ubicación</h3>
              <p className="RouteMap__gateText">
                Para mostrarte las rutas cercanas necesitamos saber dónde estás. Si
                lo prefieres, puedes continuar y usaremos València como referencia.
              </p>
              <button
                type="button"
                className="RouteMap__gateBtn"
                onClick={requestLocation}
              >
                Permitir ubicación
              </button>
              <button
                type="button"
                className="RouteMap__gateSkip"
                onClick={skipLocation}
              >
                Continuar sin ubicación
              </button>
            </div>
          ) : (
            <div className="RouteMap__gateCard RouteMap__gateCard--loading">
              <span className="RouteMap__gateSpinner" aria-hidden="true" />
              <p className="RouteMap__gateText">
                {phase === 'locating' ? 'Localizando…' : 'Preparando el mapa…'}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Controls only make sense once the map exists. */}
      {ready && (
        <>
          {/* Layer switch (top-left) */}
          <div className="RouteMap__layers" role="group" aria-label="Estilo de mapa">
            {LAYER_ORDER.map((key) => (
              <button
                key={key}
                type="button"
                className={`RouteMap__layer${activeLayer === key ? ' is-active' : ''}`}
                aria-pressed={activeLayer === key}
                onClick={() => switchLayer(key)}
              >
                {LAYERS[key].label}
              </button>
            ))}
          </div>

          {/* Zoom + recenter (top-right) */}
          <div className="RouteMap__controls">
            <div className="RouteMap__zoom">
              <button type="button" onClick={handleZoomIn} aria-label="Acercar">
                +
              </button>
              <span className="RouteMap__zoomSep" aria-hidden="true" />
              <button type="button" onClick={handleZoomOut} aria-label="Alejar">
                −
              </button>
            </div>
            <button
              type="button"
              className={`RouteMap__recenter${locating ? ' is-busy' : ''}`}
              onClick={recenter}
              aria-label="Centrar en mi ubicación"
            >
              <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                <circle cx="12" cy="12" r="3.2" fill="currentColor" />
                <circle cx="12" cy="12" r="7" fill="none" stroke="currentColor" strokeWidth="1.6" />
                <line x1="12" y1="1.5" x2="12" y2="5" stroke="currentColor" strokeWidth="1.6" />
                <line x1="12" y1="19" x2="12" y2="22.5" stroke="currentColor" strokeWidth="1.6" />
                <line x1="1.5" y1="12" x2="5" y2="12" stroke="currentColor" strokeWidth="1.6" />
                <line x1="19" y1="12" x2="22.5" y2="12" stroke="currentColor" strokeWidth="1.6" />
              </svg>
            </button>
          </div>
        </>
      )}
    </div>
  );
});

function PinIcon() {
  return (
    <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true">
      <path
        d="M12 22s7-6.2 7-12a7 7 0 1 0-14 0c0 5.8 7 12 7 12Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="10" r="2.6" fill="currentColor" />
    </svg>
  );
}

export default RouteMap;
